import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import {
  Plus, Pencil, Trash2, Calendar, TrendingUp, TrendingDown, Download,
  Users, FileSpreadsheet, X, ChevronDown, ChevronUp
} from 'lucide-react';
import {
  apiListEntries, apiCreateEntry, apiUpdateEntry, apiDeleteEntry,
  apiListOperators, apiListWorkTypes, apiListCustomers, apiProdExport
} from '../../api';
import { toast } from 'sonner';

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtEur = (v) => '€' + (v || 0).toFixed(2);

const EntryForm = ({ initial, customers, operators, allWorkTypes, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [customerId, setCustomerId] = useState(initial?.customer_id || '');
  const [workTypeId, setWorkTypeId] = useState(initial?.work_type_id || '');
  const [date, setDate] = useState(initial?.date || todayStr());
  const [notes, setNotes] = useState(initial?.notes || '');
  const [ops, setOps] = useState(
    initial?.operators_detail?.length
      ? initial.operators_detail.map((o, i) => ({ _rid: `r${Date.now()}-${i}`, operator_id: o.operator_id, pieces: o.pieces, hours: o.hours }))
      : [{ _rid: `r${Date.now()}-0`, operator_id: '', pieces: 0, hours: 0 }]
  );

  // Filter work types by selected customer
  const filteredWorkTypes = useMemo(
    () => allWorkTypes.filter(w => w.customer_id === customerId && w.active),
    [allWorkTypes, customerId]
  );
  const activeCustomers = useMemo(() => customers.filter(c => c.active), [customers]);
  const activeOperators = useMemo(() => operators.filter(op => op.active), [operators]);

  const handleCustomerChange = (v) => {
    setCustomerId(v);
    setWorkTypeId(''); // reset work type when customer changes
  };

  const selectedWt = allWorkTypes.find(w => w.id === workTypeId);
  const rate = selectedWt?.rate_eur_per_piece || 0;

  const addOp = () => setOps(prev => [...prev, { _rid: `r${Date.now()}-${prev.length}`, operator_id: '', pieces: 0, hours: 0 }]);
  const removeOp = (idx) => setOps(prev => prev.filter((_, i) => i !== idx));
  const updateOp = (idx, field, value) => {
    setOps(prev => prev.map((o, i) => i === idx ? { ...o, [field]: value } : o));
  };

  // Aggregated preview
  const opsPreview = ops.map(o => {
    const pieces = parseInt(o.pieces) || 0;
    const hours = parseFloat(o.hours) || 0;
    const total = pieces * rate;
    const eph = hours > 0 ? total / hours : 0;
    return { ...o, pieces, hours, total, eph };
  });
  const totals = opsPreview.reduce((a, o) => ({
    pieces: a.pieces + o.pieces,
    hours: a.hours + o.hours,
    total: a.total + o.total
  }), { pieces: 0, hours: 0, total: 0 });
  totals.avgEph = totals.hours > 0 ? totals.total / totals.hours : 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!customerId) { toast.error('Seleziona il cliente'); return; }
    if (!workTypeId) { toast.error('Seleziona la lavorazione'); return; }
    if (!date) { toast.error('Inserisci la data'); return; }
    const cleanOps = ops.filter(o => o.operator_id);
    if (cleanOps.length === 0) { toast.error('Aggiungi almeno un operatore'); return; }
    const seenOps = new Set();
    for (const o of cleanOps) {
      if (seenOps.has(o.operator_id)) {
        toast.error('Operatore duplicato nella stessa sessione');
        return;
      }
      seenOps.add(o.operator_id);
      if (parseFloat(o.hours) <= 0) { toast.error('Ore devono essere > 0 per ogni operatore'); return; }
      if (parseInt(o.pieces) < 0) { toast.error('Pezzi non validi'); return; }
    }
    onSubmit({
      customer_id: customerId,
      work_type_id: workTypeId,
      date, notes: notes.trim(),
      operators: cleanOps.map(o => ({
        operator_id: o.operator_id,
        pieces: parseInt(o.pieces) || 0,
        hours: parseFloat(o.hours) || 0
      }))
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="entry-form">
      {/* Step 1: Cliente */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">1</span>
          Cliente <span className="text-red-500">*</span>
        </Label>
        <Select value={customerId} onValueChange={handleCustomerChange} disabled={isEdit}>
          <SelectTrigger data-testid="entry-customer-select"><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
          <SelectContent>
            {activeCustomers.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Step 2: Lavorazione */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">2</span>
          Lavorazione <span className="text-red-500">*</span>
          {selectedWt && (
            <span className="text-xs text-slate-500 font-normal">— tariffa €{rate.toFixed(3)}/pz</span>
          )}
        </Label>
        <Select value={workTypeId} onValueChange={setWorkTypeId} disabled={!customerId || isEdit}>
          <SelectTrigger data-testid="entry-worktype-select">
            <SelectValue placeholder={customerId ? 'Seleziona lavorazione...' : 'Prima seleziona il cliente'} />
          </SelectTrigger>
          <SelectContent>
            {filteredWorkTypes.length === 0 ? (
              <div className="p-2 text-xs text-slate-500">Nessuna lavorazione attiva per questo cliente</div>
            ) : (
              filteredWorkTypes.map(w => (
                <SelectItem key={w.id} value={w.id}>
                  {w.code} — {w.description} (€{w.rate_eur_per_piece.toFixed(3)}/pz)
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Step 3: Operatori multi */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">3</span>
            Operatori <span className="text-red-500">*</span>
            <span className="text-xs text-slate-500 font-normal">— {ops.length} aggiunt{ops.length === 1 ? 'o' : 'i'}</span>
          </Label>
          <Button data-testid="entry-add-operator-btn" type="button" size="sm" variant="outline" onClick={addOp}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi operatore
          </Button>
        </div>
        <div className="space-y-2">
          {opsPreview.map((o, idx) => (
            <div key={o._rid} className="p-3 rounded-lg border border-slate-200 bg-slate-50/50 space-y-2" data-testid={`entry-operator-row-${idx}`}>
              <div className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-12 sm:col-span-5">
                  <Label className="text-[11px] mb-1 block">Operatore</Label>
                  <Select value={o.operator_id} onValueChange={(v) => updateOp(idx, 'operator_id', v)}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      {activeOperators.map(op => (
                        <SelectItem key={op.id} value={op.id}>{op.alias} — {op.first_name} {op.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-5 sm:col-span-3">
                  <Label className="text-[11px] mb-1 block">Pezzi</Label>
                  <Input type="number" min="0" value={o.pieces} onChange={(e) => updateOp(idx, 'pieces', e.target.value)} className="h-9" />
                </div>
                <div className="col-span-5 sm:col-span-3">
                  <Label className="text-[11px] mb-1 block">Ore</Label>
                  <Input type="number" step="0.1" min="0" value={o.hours} onChange={(e) => updateOp(idx, 'hours', e.target.value)} className="h-9" />
                </div>
                <div className="col-span-2 sm:col-span-1 pt-5">
                  {ops.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-600 hover:bg-red-50" onClick={() => removeOp(idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {selectedWt && o.pieces > 0 && o.hours > 0 && (
                <div className="flex items-center gap-3 text-xs pt-1 pl-1">
                  <span className="text-slate-500">→</span>
                  <span className="font-semibold text-blue-700">{fmtEur(o.total)}</span>
                  <span className="text-slate-400">·</span>
                  <span className="font-semibold text-emerald-700">{fmtEur(o.eph)}/ora</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Aggregated total */}
        {selectedWt && totals.pieces > 0 && (
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-blue-700" />
              <span className="text-xs font-semibold text-blue-900 uppercase tracking-wider">Totale aggregato lavorazione</span>
            </div>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Pezzi</div>
                <div className="font-bold text-slate-900 tabular-nums">{totals.pieces}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Ore</div>
                <div className="font-bold text-slate-900 tabular-nums">{totals.hours.toFixed(1)}h</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Totale €</div>
                <div className="font-bold text-blue-700 tabular-nums">{fmtEur(totals.total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Media €/ora</div>
                <div className="font-bold text-emerald-700 tabular-nums">{fmtEur(totals.avgEph)}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 4: Data + note */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">4</span>
            Data <span className="text-red-500">*</span>
          </Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-500 text-[11px] uppercase tracking-wider pt-0.5">Note (opzionale)</Label>
          <Input placeholder="es. Lotto 123, commessa speciale..." value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button data-testid="entry-submit-btn" type="submit" className="bg-blue-700 hover:bg-blue-800">
          {isEdit ? 'Salva modifiche' : 'Registra sessione'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const EntryRow = ({ e, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const target = e.target_eur_per_hour || 0;
  const aggOnTarget = target > 0 ? e.avg_eur_per_hour >= target : null;
  return (
    <>
      <tr className="hover:bg-slate-50" data-testid={`entry-row-${e.id}`}>
        <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{fmtDate(e.date)}</td>
        <td className="px-3 py-2.5">
          <div className="text-xs font-mono text-slate-500">{e.customer_code}</div>
          <div className="text-sm text-slate-800 font-medium truncate max-w-[160px]">{e.customer_name}</div>
        </td>
        <td className="px-3 py-2.5">
          <div className="font-mono text-xs text-slate-500">{e.work_type_code}</div>
          <div className="text-slate-700 truncate max-w-[200px]">{e.work_type_description}</div>
        </td>
        <td className="px-3 py-2.5">
          <button
            data-testid={`entry-expand-${e.id}`}
            onClick={() => setExpanded(v => !v)}
            className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-blue-700"
          >
            <Users className="w-3.5 h-3.5" />
            <span className="font-medium">{e.operators_detail.length}</span>
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </td>
        <td className="px-3 py-2.5 text-right tabular-nums font-medium">{e.total_pieces}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{e.total_hours.toFixed(1)}h</td>
        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-blue-700">{fmtEur(e.total_eur)}</td>
        <td className="px-3 py-2.5 text-right">
          <div className={`inline-flex items-center gap-1 tabular-nums font-semibold ${aggOnTarget === null ? 'text-slate-700' : aggOnTarget ? 'text-emerald-700' : 'text-red-700'}`}>
            {aggOnTarget === true && <TrendingUp className="w-3 h-3" />}
            {aggOnTarget === false && <TrendingDown className="w-3 h-3" />}
            {fmtEur(e.avg_eur_per_hour)}
          </div>
        </td>
        <td className="px-3 py-2.5 text-right">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(e)}>
            <Pencil className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => onDelete(e)}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/70">
          <td colSpan={9} className="px-6 py-3">
            <div className="text-[10px] uppercase font-semibold text-slate-500 mb-2 tracking-wider">Dettaglio operatori</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  <th className="text-left py-1 font-medium">Operatore</th>
                  <th className="text-right py-1 font-medium">Pezzi</th>
                  <th className="text-right py-1 font-medium">Ore</th>
                  <th className="text-right py-1 font-medium">Totale €</th>
                  <th className="text-right py-1 font-medium">€/ora</th>
                  <th className="text-center py-1 font-medium">Target</th>
                </tr>
              </thead>
              <tbody>
                {e.operators_detail.map(op => {
                  const opT = op.operator_target > 0 ? op.eur_per_hour >= op.operator_target : null;
                  return (
                    <tr key={op.operator_id} className="border-b border-slate-100 last:border-0">
                      <td className="py-1.5">
                        <span className="font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded text-[10px] mr-1.5">{op.operator_alias}</span>
                        <span className="text-slate-700">{op.operator_name}</span>
                      </td>
                      <td className="text-right tabular-nums">{op.pieces}</td>
                      <td className="text-right tabular-nums">{op.hours.toFixed(1)}h</td>
                      <td className="text-right tabular-nums font-semibold text-blue-700">{fmtEur(op.total_eur)}</td>
                      <td className="text-right tabular-nums font-semibold">
                        <span className={opT === null ? 'text-slate-700' : opT ? 'text-emerald-700' : 'text-red-700'}>
                          {fmtEur(op.eur_per_hour)}
                        </span>
                      </td>
                      <td className="text-center text-slate-500 tabular-nums">{op.operator_target > 0 ? fmtEur(op.operator_target) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {e.notes && (
              <div className="mt-2 text-xs text-slate-600 italic">Note: {e.notes}</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const EntriesManager = () => {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [filterOperator, setFilterOperator] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const reload = async () => {
    const params = {};
    if (filterCustomer !== 'all') params.customer_id = filterCustomer;
    if (filterOperator !== 'all') params.operator_id = filterOperator;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const [entries, cs, ops, wts] = await Promise.all([
      apiListEntries(params), apiListCustomers(), apiListOperators(), apiListWorkTypes()
    ]);
    setList(entries || []);
    setCustomers(cs || []);
    setOperators(ops || []);
    setWorkTypes(wts || []);
  };
  useEffect(() => { reload(); }, [filterCustomer, filterOperator, dateFrom, dateTo]); // eslint-disable-line

  const totals = useMemo(() => {
    const pieces = list.reduce((a, b) => a + (b.total_pieces || 0), 0);
    const hours = list.reduce((a, b) => a + (b.total_hours || 0), 0);
    const total = list.reduce((a, b) => a + (b.total_eur || 0), 0);
    return { pieces, hours, total, avg: hours > 0 ? total / hours : 0 };
  }, [list]);

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        await apiUpdateEntry(editing.id, { date: data.date, notes: data.notes, operators: data.operators });
        toast.success('Sessione aggiornata');
      } else {
        await apiCreateEntry(data);
        toast.success('Sessione registrata');
      }
      setDialogOpen(false);
      setEditing(null);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteEntry(toDelete.id);
      toast.success('Sessione eliminata');
      setToDelete(null);
      reload();
    } catch { toast.error('Errore'); }
  };

  const exportFile = async (fmt) => {
    try {
      const params = { fmt };
      if (filterCustomer !== 'all') params.customer_id = filterCustomer;
      if (filterOperator !== 'all') params.operator_id = filterOperator;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const resp = await apiProdExport(params);
      const cd = resp.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `produzione_${todayStr()}.${fmt}`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Esportato ${fmt.toUpperCase()}`);
    } catch (err) {
      toast.error('Errore esportazione');
    }
  };

  const canSetup = customers.length > 0 && workTypes.length > 0 && operators.length > 0;

  return (
    <div className="space-y-4" data-testid="entries-manager">
      {/* Filters and actions */}
      <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center justify-between flex-wrap">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger data-testid="entry-filter-customer" className="h-9 w-[200px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterOperator} onValueChange={setFilterOperator}>
            <SelectTrigger data-testid="entry-filter-operator" className="h-9 w-[180px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli operatori</SelectItem>
              {operators.map(o => <SelectItem key={o.id} value={o.id}>{o.alias} — {o.first_name} {o.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[140px] text-sm" placeholder="Da" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[140px] text-sm" placeholder="A" />
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-csv-btn" variant="outline" onClick={() => exportFile('csv')} className="h-9 gap-2 text-sm" disabled={list.length === 0}>
            <Download className="w-4 h-4" /> CSV
          </Button>
          <Button data-testid="export-xlsx-btn" variant="outline" onClick={() => exportFile('xlsx')} className="h-9 gap-2 text-sm" disabled={list.length === 0}>
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button
                data-testid="entry-new-btn"
                onClick={() => { setEditing(null); setDialogOpen(true); }}
                className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"
                disabled={!canSetup}
                title={!canSetup ? 'Configura prima clienti, lavorazioni e operatori' : ''}
              >
                <Plus className="w-4 h-4" /> Nuova sessione
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? 'Modifica sessione' : 'Nuova sessione produzione'}</DialogTitle>
                <DialogDescription>
                  Seleziona cliente, lavorazione, e aggiungi uno o più operatori con pezzi e ore individuali.
                </DialogDescription>
              </DialogHeader>
              <EntryForm
                initial={editing}
                customers={customers}
                operators={operators}
                allWorkTypes={workTypes}
                onSubmit={handleSubmit}
                onCancel={() => { setDialogOpen(false); setEditing(null); }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase text-slate-500 tracking-wide">Pezzi totali</div>
          <div className="text-lg font-bold text-slate-900 tabular-nums">{totals.pieces}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase text-slate-500 tracking-wide">Ore totali</div>
          <div className="text-lg font-bold text-slate-900 tabular-nums">{totals.hours.toFixed(1)}h</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase text-slate-500 tracking-wide">Totale €</div>
          <div className="text-lg font-bold text-blue-700 tabular-nums">{fmtEur(totals.total)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase text-slate-500 tracking-wide">Media €/ora</div>
          <div className="text-lg font-bold text-emerald-700 tabular-nums">{fmtEur(totals.avg)}</div>
        </div>
      </div>

      {/* Table */}
      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessuna sessione registrata</div>
          {!canSetup && (
            <div className="text-xs text-slate-500 mt-1">Configura prima clienti, lavorazioni e operatori</div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-600">Data</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-600">Cliente</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-600">Lavorazione</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase text-slate-600">Operatori</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Pezzi</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Ore</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Totale €</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600">Media €/ora</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase text-slate-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map(e => (
                <EntryRow
                  key={e.id} e={e}
                  onEdit={(ent) => { setEditing(ent); setDialogOpen(true); }}
                  onDelete={(ent) => setToDelete(ent)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la sessione?</AlertDialogTitle>
            <AlertDialogDescription>Azione non reversibile. Saranno cancellati i dati di tutti gli operatori inclusi.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EntriesManager;
